import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CreateGpsIntegrationCommand } from '@area-restrita/api-client/model/createGpsIntegrationCommand';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import {
  gpsProviderConfigs,
  getProviderSchema,
  getProviderDefaultValues,
} from './gpsProviderConfigs';
import GpsConfigFields from './GpsConfigFields';

interface GpsIntegrationFormProps {
  onSubmit: (data: CreateGpsIntegrationCommand) => void;
  isLoading?: boolean;
}

// Schema base do formulário
const baseFormSchema = z.object({
  gpsProvider: z.enum(['webhook', 'mobs2', 'addlog'] as const),
  config: z.record(z.any()),
});

type FormData = z.infer<typeof baseFormSchema>;

const GpsIntegrationForm: React.FC<GpsIntegrationFormProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const form = useForm<FormData>({
    resolver: zodResolver(baseFormSchema),
    defaultValues: {
      gpsProvider: 'webhook',
      config: getProviderDefaultValues('webhook'),
    },
  });

  const watchedProvider = form.watch('gpsProvider');

  // Atualiza a configuração quando o provedor muda
  useEffect(() => {
    if (!watchedProvider) return;

    const newConfig = getProviderDefaultValues(watchedProvider);
    form.setValue('config', newConfig);
  }, [watchedProvider, form]);

  const handleSubmit = (data: FormData) => {
    // Valida a configuração específica do provedor
    const providerSchema = getProviderSchema(data.gpsProvider);
    const validatedConfig = providerSchema.parse(data.config);

    const payload: CreateGpsIntegrationCommand = {
      gpsProvider: data.gpsProvider,
      config: validatedConfig,
      priority: 0, // Padrão conforme especificado
    };

    onSubmit(payload);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="gpsProvider"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Provedor GPS</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um provedor GPS" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(gpsProviderConfigs).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <GpsConfigFields
          control={form.control}
          provider={watchedProvider}
          configFieldName="config"
        />

        <div className="flex justify-end space-x-2 pt-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Criando...' : 'Criar Integração'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default GpsIntegrationForm;
